import { SUBMISSSION_DETAILS_V2_PATH } from '../constants';
import {
  AssignOrganizationField,
  AssignOrganizationtData,
  AssignOrganizationtDialogData,
  EntityType,
  OrganizationAssignmentRole,
  OrganizationValidationInput,
} from '../types';
import { DialogSize } from '@wcg/frontend-framework';

export const ORGANIZATION_ROLE_FORM_FIELD: Record<
  OrganizationAssignmentRole,
  AssignOrganizationField
> = {
  sponsor: 'sponsor_organization_id',
  pi: 'pi_organization_id',
  facility_owner: 'facility_owner_organization_id',
};

export const ORGANIZATION_ROLE_LABEL: Record<
  OrganizationAssignmentRole,
  string
> = {
  sponsor: 'Sponsor Organizations',
  pi: 'PI Organizations',
  facility_owner: 'Facility Owner Organizations',
};

/** Stable UI / payload order when API returns roles in any order */
export const ORGANIZATION_ROLE_ORDER: readonly OrganizationAssignmentRole[] = [
  'sponsor',
  'pi',
  'facility_owner',
] as const;

function _normalizeOrganizationRoleKey(raw: unknown): string {
  let s = String(raw ?? '').trim();
  if (!s) return '';
  s = s.replace(/\s+/g, '_').replace(/-/g, '_');
  s = s.replace(/([a-z])([A-Z])/g, '$1_$2');
  return s.toLowerCase();
}

/**
 * Maps API organization-validation role strings (including camelCase / aliases) to canonical roles.
 */
export function coerceOrganizationAssignmentRole(
  raw: unknown,
): OrganizationAssignmentRole | undefined {
  const key = _normalizeOrganizationRoleKey(raw);
  if (!key) return undefined;

  const alias: Record<string, OrganizationAssignmentRole> = {
    sponsor: 'sponsor',
    pi: 'pi',
    principal_investigator: 'pi',
    facility_owner: 'facility_owner',
    facilityowner: 'facility_owner',
  };

  if (alias[key]) {
    return alias[key];
  }

  if ((ORGANIZATION_ROLE_ORDER as readonly string[]).includes(key)) {
    return key as OrganizationAssignmentRole;
  }

  return undefined;
}

export function normalizeOrganizationAssignmentRoles(
  raw: readonly string[] | null | undefined,
): OrganizationAssignmentRole[] {
  if (!raw?.length) return [];
  const seen = new Set<OrganizationAssignmentRole>();
  const out: OrganizationAssignmentRole[] = [];
  for (const item of raw) {
    const role = coerceOrganizationAssignmentRole(item);
    if (role && !seen.has(role)) {
      seen.add(role);
      out.push(role);
    }
  }
  return [...out].sort(
    (a, b) =>
      ORGANIZATION_ROLE_ORDER.indexOf(a) - ORGANIZATION_ROLE_ORDER.indexOf(b),
  );
}

export const EARLY_STAGES = ['draft', 'qc_check'];

export function isEarlySubmissionStage(
  stage?: string | null | undefined,
): boolean {
  return EARLY_STAGES.includes(stage ?? '');
}

export function getSubmissionDocumentsPath(
  stage?: string | null | undefined,
): string {
  return isEarlySubmissionStage(stage) ? 'documents' : 'documents/all';
}

export const buildSubmissionBasePath = (
  submissionIntId: string | number,
): string => {
  return `/${SUBMISSSION_DETAILS_V2_PATH}/${submissionIntId}`;
};

export const buildSubmissionUploadDocumentsPath = (
  submissionIntId: string | number | null | undefined,
): string | null => {
  if (submissionIntId == null || submissionIntId === '') return null;
  return `${SUBMISSSION_DETAILS_V2_PATH}/${submissionIntId}/upload-documents`;
};

export const buildSubmissionDocumentsUrl = (
  currentStage: string | null | undefined,
  submissionIntId: string | number,
): string => {
  const basePath = buildSubmissionBasePath(submissionIntId);
  const targetPath = getSubmissionDocumentsPath(currentStage);
  return `${basePath}/${targetPath}`;
};

export function organizationValidationPayload(
  entityType: EntityType,
  data?: AssignOrganizationtData,
  unassignedOrganizationTypes?: OrganizationAssignmentRole[],
): AssignOrganizationtData {
  if (!data) return {};

  if (unassignedOrganizationTypes?.length) {
    const out: AssignOrganizationtData = {};
    for (const role of unassignedOrganizationTypes) {
      const field = ORGANIZATION_ROLE_FORM_FIELD[role];
      const value = data[field];
      if (value) {
        out[field] = value;
      }
    }
    return out;
  }

  if (!entityType) return {};

  if (entityType === 'study' && data.sponsor_organization_id) {
    return {
      sponsor_organization_id: data.sponsor_organization_id,
    };
  }

  if (
    entityType === 'site' &&
    data.pi_organization_id &&
    data.facility_owner_organization_id
  ) {
    return {
      pi_organization_id: data.pi_organization_id,
      facility_owner_organization_id: data.facility_owner_organization_id,
    };
  }

  return {};
}

export function assignOrganizationDialogData(params: {
  dialogTitle: string;
  unassignedOrganizationTypes: OrganizationAssignmentRole[];
  assignedOrganizationTypes?: OrganizationAssignmentRole[];
  dialogDescription: string;
  dialogBanner: string;
  dialogNote: string;
}): AssignOrganizationtDialogData {
  return {
    dialogTitle: params.dialogTitle,
    dialogSize: DialogSize.MEDIUM,
    unassignedOrganizationTypes: params.unassignedOrganizationTypes,
    assignedOrganizationTypes: params.assignedOrganizationTypes,
    dialogDescription: params.dialogDescription,
    dialogBanner: params.dialogBanner,
    dialogNote: params.dialogNote,
  };
}

export function buildOrganizationValidationData(
  data: OrganizationValidationInput,
  organizationTypes: OrganizationAssignmentRole[],
): AssignOrganizationtData | null {
  if (data.skip_organization_check) {
    return null;
  }

  const out: AssignOrganizationtData = {};
  for (const role of organizationTypes) {
    const field = ORGANIZATION_ROLE_FORM_FIELD[role];
    const value = data[field];
    if (value) {
      out[field] = value;
    }
  }

  return Object.keys(out).length ? out : null;
}

/**
 * Determines if a user can edit a submission based on the current stage, whether they are internal, and their edit permissions.
 * @param currentStage - The current stage of the submission (e.g., 'draft', 'qc_check').
 * @param isInternal - Whether the user is internal. Angular signals read `undefined` until
 *   they resolve (e.g. `toSignal()` before its source emits), so this is treated as `false`.
 * @param canEdit - Whether the user has edit permissions. Same `undefined`-as-`false` handling.
 * @param openForEdits - An optional boolean indicating if the submission is open for edits (default is false).
 * @returns A boolean indicating if the user can edit the submission.
 */
export const canEditCollabhubEntities = (
  currentStage: string,
  isInternal: boolean = false,
  canEdit: boolean = false,
  openForEdits: boolean = false,
) => {
  return (
    (currentStage === 'draft' && canEdit) ||
    (currentStage === 'qc_check' && isInternal && canEdit) ||
    (currentStage === 'qc_check' && !isInternal && canEdit && openForEdits)
  );
};
